import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  MusicNote as MusicIcon,
  Album as AlbumIcon,
  Image as ImageIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import ApiService from '../../services/api-service';
import notifier from '../../components/Notifier';
import { useHistory } from 'react-router-dom';

const steps = ['Select User', 'Album Details', 'Upload Tracks', 'Set Royalties', 'NFT Settings', 'Review & Submit'];

export default function BulkAlbumUpload() {
  const history = useHistory();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [initialUserLoaded, setInitialUserLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  
  // Get userId from URL params
  const urlParams = new URLSearchParams(location.search);
  const preSelectedUserId = urlParams.get('userId');
  
  // Form data
  const [selectedUser, setSelectedUser] = useState(null);
  const [albumData, setAlbumData] = useState({
    title: '',
    description: '',
    genre: 'Pop',
    releaseDate: new Date().toISOString().split('T')[0],
    isExplicit: false,
    isPrivate: false
  });
  
  const [albumCover, setAlbumCover] = useState(null);
  const [albumCoverPreview, setAlbumCoverPreview] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [royalties, setRoyalties] = useState({
    ownerRoyalty: 10,
    collaborators: []
  });
  
  // NFT/Collectible settings
  const [nftSettings, setNftSettings] = useState({
    createAsNft: true,
    currency: 'USD',
    count: 100,
    platform: 'Siki'
  });
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(false);
  
  const genres = [
    'Pop', 'Rock', 'Hip Hop', 'R&B', 'Country', 'Electronic', 
    'Jazz', 'Classical', 'Reggae', 'Blues', 'Folk', 'Other'
  ];

  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Pre-select user if userId is provided in URL
  useEffect(() => {
    if (preSelectedUserId && users.length > 0 && !initialUserLoaded) {
      const user = users.find(u => u._id === preSelectedUserId);
      console.log('Looking for pre-selected user:', preSelectedUserId);
      console.log('Found user:', user);
      if (user) {
        setSelectedUser(user);
        setInitialUserLoaded(true);
        // Automatically move to the next step
        setTimeout(() => setActiveStep(1), 100);
      }
    }
  }, [preSelectedUserId, users, initialUserLoaded]);

  const fetchUsers = async () => {
    try {
      setSearchingUsers(true);
      const response = await ApiService.ADMIN_GET_ALL_USERS({ limit: 100, page: 1 });
      console.log('Users API Response:', response);
      
      // Handle different response structures
      if (response?.data?.result) {
        setUsers(response.data.result);
      } else if (response?.data?.users) {
        setUsers(response.data.users);
      } else if (Array.isArray(response?.data)) {
        setUsers(response.data);
      } else {
        console.warn('Unexpected response structure:', response);
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error?.response?.status === 401) {
        notifier.error('Session expired. Please refresh the page and login again.');
      } else {
        notifier.error('Failed to load users');
      }
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleUserSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) return;
    
    try {
      setSearchingUsers(true);
      const response = await ApiService.ADMIN_SEARCH_USER(searchTerm);
      console.log('User search response:', response);
      
      let users = [];
      // Handle different response structures - check response.result first (not response.data.result)
      if (response?.result) {
        if (Array.isArray(response.result)) {
          users = response.result;
        } else if (response.result.response && Array.isArray(response.result.response)) {
          users = response.result.response;
        } else if (response.result.data && Array.isArray(response.result.data)) {
          users = response.result.data;
        } else if (response.result.users && Array.isArray(response.result.users)) {
          users = response.result.users;
        }
      } else if (response?.response && Array.isArray(response.response)) {
        users = response.response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          users = response.data;
        } else if (response.data.result && Array.isArray(response.data.result)) {
          users = response.data.result;
        } else if (response.data.users && Array.isArray(response.data.users)) {
          users = response.data.users;
        }
      }
      
      console.log('Parsed users:', users);
      setUsers(users);
    } catch (error) {
      console.error('Failed to search users:', error);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleAlbumCoverChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        notifier.error('Please select an image file');
        return;
      }
      setAlbumCover(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAlbumCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTracksChange = (event) => {
    const files = Array.from(event.target.files);
    const audioFiles = files.filter(file => 
      file.type.startsWith('audio/') || 
      file.name.match(/\.(mp3|wav|m4a|flac)$/i)
    );
    
    if (audioFiles.length !== files.length) {
      notifier.warning('Some non-audio files were filtered out');
    }
    
    const trackList = audioFiles.map((file, index) => ({
      id: Date.now() + index,
      file: file,
      name: file.name.replace(/\.[^/.]+$/, '').replace(/^\d+[-_\s]+/, ''),
      size: file.size,
      status: 'pending'
    }));
    
    setTracks([...tracks, ...trackList]);
  };

  const removeTrack = (trackId) => {
    setTracks(tracks.filter(track => track.id !== trackId));
  };

  const addCollaborator = () => {
    setRoyalties({
      ...royalties,
      collaborators: [
        ...royalties.collaborators,
        { name: '', email: '', royalty: 5 }
      ]
    });
  };

  const updateCollaborator = (index, field, value) => {
    const updated = [...royalties.collaborators];
    updated[index][field] = value;
    setRoyalties({ ...royalties, collaborators: updated });
  };

  const removeCollaborator = (index) => {
    const updated = royalties.collaborators.filter((_, i) => i !== index);
    setRoyalties({ ...royalties, collaborators: updated });
  };

  const getTotalRoyalties = () => {
    const collabTotal = royalties.collaborators.reduce((sum, c) => sum + (Number(c.royalty) || 0), 0);
    return Number(royalties.ownerRoyalty) + collabTotal;
  };

  const validateStep = (step) => {
    switch(step) {
      case 0:
        return selectedUser !== null;
      case 1:
        return albumData.title && albumData.genre;
      case 2:
        return tracks.length > 0;
      case 3:
        return getTotalRoyalties() <= 100;
      case 4:
        return nftSettings.count > 0 && nftSettings.currency && nftSettings.platform;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      notifier.error('Please complete all required fields');
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setConfirmDialog(false);
    setLoading(true);
    setUploadProgress(0);
    
    try {
      // Upload tracks as an album - simpler approach
      notifier.info('Uploading album tracks...');
      
      const totalTracks = tracks.length;
      let uploadedCount = 0;
      
      // Generate a unique album ID for grouping
      const albumGroupId = `album_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        
        try {
          setUploadStatus(prev => ({
            ...prev,
            [track.id]: { status: 'uploading', message: 'Uploading...' }
          }));
          
          const trackFormData = new FormData();
          trackFormData.append('title', track.name);
          trackFormData.append('genre', albumData.genre);
          trackFormData.append('releaseDate', albumData.releaseDate);
          
          // Album-specific metadata
          trackFormData.append('releaseType', 'albums'); // Must be 'albums' (plural) to match frontend
          trackFormData.append('albumTitle', albumData.title);
          trackFormData.append('albumGroupId', albumGroupId); // Group tracks together
          trackFormData.append('trackNumber', i + 1); // Track ordering
          
          trackFormData.append('isExplicit', albumData.isExplicit);
          trackFormData.append('private', albumData.isPrivate);
          trackFormData.append('ownerRoyalty', royalties.ownerRoyalty);
          trackFormData.append('artist', selectedUser.fullName);
          trackFormData.append('file', track.file);
          
          // Add NFT/Collectible parameters
          if (nftSettings.createAsNft) {
            trackFormData.append('currency', nftSettings.currency);
            trackFormData.append('count', parseInt(nftSettings.count));
            trackFormData.append('platform', nftSettings.platform);
          }
          
          if (albumCover) {
            trackFormData.append('thumbnailImg', albumCover);
          }
          
          if (royalties.collaborators.length > 0) {
            trackFormData.append('collaborators', JSON.stringify(royalties.collaborators));
          }
          
          await ApiService.ADMIN_UPLOAD_MUSIC_FOR_USER(selectedUser._id, trackFormData);
          
          uploadedCount++;
          setUploadProgress((uploadedCount / totalTracks) * 100);
          setUploadStatus(prev => ({
            ...prev,
            [track.id]: { status: 'success', message: 'Uploaded successfully' }
          }));
          
        } catch (error) {
          console.error(`Failed to upload track ${track.name}:`, error);
          setUploadStatus(prev => ({
            ...prev,
            [track.id]: { status: 'error', message: error.message || 'Upload failed' }
          }));
        }
      }
      
      notifier.success(`Album upload complete! ${uploadedCount}/${totalTracks} tracks uploaded.`);
      
      // Reset form after successful upload
      setTimeout(() => {
        history.push(`/admin/user/${selectedUser._id}/edit`);
      }, 2000);
      
    } catch (error) {
      console.error('Album upload failed:', error);
      notifier.error('Failed to upload album: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch(activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select User
            </Typography>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option ? `${option.fullName || option.firstName + ' ' + option.lastName || 'Unknown'} (${option.email})` : ''}
              loading={searchingUsers}
              value={selectedUser}
              onChange={(event, newValue) => {
                console.log('User selected:', newValue);
                setSelectedUser(newValue);
              }}
              onInputChange={(event, newInputValue, reason) => {
                console.log('Input changed:', newInputValue, 'Reason:', reason);
                if (reason === 'input' && newInputValue.length >= 2) {
                  handleUserSearch(newInputValue);
                }
              }}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and select user"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedUser && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1">
                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {selectedUser.fullName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedUser.email}
                  </Typography>
                  {selectedUser.isArtist && (
                    <Chip label="Artist" color="primary" size="small" sx={{ mt: 1 }} />
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        );
        
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Album Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Album Title"
                  fullWidth
                  required
                  value={albumData.title}
                  onChange={(e) => setAlbumData({ ...albumData, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Genre</InputLabel>
                  <Select
                    value={albumData.genre}
                    onChange={(e) => setAlbumData({ ...albumData, genre: e.target.value })}
                    label="Genre"
                  >
                    {genres.map(genre => (
                      <MenuItem key={genre} value={genre}>{genre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={albumData.description}
                  onChange={(e) => setAlbumData({ ...albumData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Release Date"
                  type="date"
                  fullWidth
                  value={albumData.releaseDate}
                  onChange={(e) => setAlbumData({ ...albumData, releaseDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={albumData.isExplicit}
                        onChange={(e) => setAlbumData({ ...albumData, isExplicit: e.target.checked })}
                      />
                    }
                    label="Explicit"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={albumData.isPrivate}
                        onChange={(e) => setAlbumData({ ...albumData, isPrivate: e.target.checked })}
                      />
                    }
                    label="Private"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<ImageIcon />}
                    fullWidth
                  >
                    Upload Album Cover
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleAlbumCoverChange}
                    />
                  </Button>
                  {albumCoverPreview && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <img
                        src={albumCoverPreview}
                        alt="Album cover"
                        style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
        
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Tracks
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{ mb: 3 }}
            >
              Select Audio Files
              <input
                type="file"
                hidden
                multiple
                accept="audio/*,.mp3,.wav,.m4a,.flac"
                onChange={handleTracksChange}
              />
            </Button>
            
            {tracks.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {tracks.length} track(s) selected
                </Typography>
                <List>
                  {tracks.map((track, index) => (
                    <ListItem key={track.id}>
                      <MusicIcon sx={{ mr: 2, color: 'action.active' }} />
                      <ListItemText
                        primary={`${index + 1}. ${track.name}`}
                        secondary={`${(track.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                      <ListItemSecondaryAction>
                        {uploadStatus[track.id] ? (
                          <Chip
                            size="small"
                            label={uploadStatus[track.id].message}
                            color={uploadStatus[track.id].status === 'success' ? 'success' : 
                                   uploadStatus[track.id].status === 'error' ? 'error' : 'default'}
                            icon={uploadStatus[track.id].status === 'success' ? <CheckIcon /> :
                                  uploadStatus[track.id].status === 'error' ? <ErrorIcon /> : null}
                          />
                        ) : (
                          <IconButton edge="end" onClick={() => removeTrack(track.id)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        );
        
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Set Royalty Splits
            </Typography>
            
            <Paper sx={{ p: 2, mb: 2 }}>
              <TextField
                label="Owner Royalty %"
                type="number"
                fullWidth
                value={royalties.ownerRoyalty}
                onChange={(e) => setRoyalties({ ...royalties, ownerRoyalty: Number(e.target.value) })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Paper>
            
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCollaborator}
              >
                Add Collaborator
              </Button>
            </Box>
            
            {royalties.collaborators.map((collab, index) => (
              <Paper key={index} sx={{ p: 2, mb: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Name"
                      fullWidth
                      value={collab.name}
                      onChange={(e) => updateCollaborator(index, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Email"
                      fullWidth
                      value={collab.email}
                      onChange={(e) => updateCollaborator(index, 'email', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Royalty %"
                      type="number"
                      fullWidth
                      value={collab.royalty}
                      onChange={(e) => updateCollaborator(index, 'royalty', Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton onClick={() => removeCollaborator(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            
            {getTotalRoyalties() > 100 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Total royalties cannot exceed 100% (Currently: {getTotalRoyalties()}%)
              </Alert>
            )}
            
            <Alert severity="info" sx={{ mt: 2 }}>
              Total Royalties: {getTotalRoyalties()}%
            </Alert>
          </Box>
        );
        
      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              NFT/Collectible Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={nftSettings.createAsNft}
                      onChange={(e) => setNftSettings({ ...nftSettings, createAsNft: e.target.checked })}
                    />
                  }
                  label="Create as NFT/Collectible"
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, ml: 4 }}>
                  When enabled, tracks will be created as collectibles that fans can purchase.
                  The NFT won't be minted on blockchain until the first sale.
                </Typography>
              </Grid>
              
              {nftSettings.createAsNft && (
                <>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={nftSettings.currency}
                        onChange={(e) => setNftSettings({ ...nftSettings, currency: e.target.value })}
                        label="Currency"
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Number of Copies"
                      type="number"
                      fullWidth
                      value={nftSettings.count}
                      onChange={(e) => setNftSettings({ ...nftSettings, count: e.target.value })}
                      inputProps={{ min: 1, max: 10000 }}
                      helperText="How many copies of each track can be sold"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Platform</InputLabel>
                      <Select
                        value={nftSettings.platform}
                        onChange={(e) => setNftSettings({ ...nftSettings, platform: e.target.value })}
                        label="Platform"
                      >
                        <MenuItem value="Siki">Siki</MenuItem>
                        <MenuItem value="Hedera">Hedera</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Each track will be created as a collectible with {nftSettings.count} available copies.
                      Fans will be able to purchase these collectibles, and the NFTs will be minted upon first sale.
                    </Alert>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        );
        
      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Submit
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    User
                  </Typography>
                  <Typography variant="body2">
                    {selectedUser?.fullName} ({selectedUser?.email})
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <AlbumIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Album Details
                  </Typography>
                  <Typography variant="body2">Title: {albumData.title}</Typography>
                  <Typography variant="body2">Genre: {albumData.genre}</Typography>
                  <Typography variant="body2">Release Date: {albumData.releaseDate}</Typography>
                  <Typography variant="body2">
                    {albumData.isExplicit && <Chip label="Explicit" size="small" sx={{ mr: 1 }} />}
                    {albumData.isPrivate && <Chip label="Private" size="small" />}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <MusicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Tracks ({tracks.length})
                  </Typography>
                  {tracks.map((track, index) => (
                    <Typography key={track.id} variant="body2">
                      {index + 1}. {track.name}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Royalty Splits
                  </Typography>
                  <Typography variant="body2">Owner: {royalties.ownerRoyalty}%</Typography>
                  {royalties.collaborators.map((collab, index) => (
                    <Typography key={index} variant="body2">
                      {collab.name}: {collab.royalty}%
                    </Typography>
                  ))}
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                    Total: {getTotalRoyalties()}%
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <AlbumIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    NFT/Collectible Settings
                  </Typography>
                  {nftSettings.createAsNft ? (
                    <>
                      <Typography variant="body2">Status: Enabled</Typography>
                      <Typography variant="body2">Currency: {nftSettings.currency}</Typography>
                      <Typography variant="body2">Copies per track: {nftSettings.count}</Typography>
                      <Typography variant="body2">Platform: {nftSettings.platform}</Typography>
                    </>
                  ) : (
                    <Typography variant="body2">NFT creation disabled</Typography>
                  )}
                </Paper>
              </Grid>
              
              {uploadProgress > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                      {Math.round(uploadProgress)}% Complete
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        );
        
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Bulk Album Upload
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderStepContent()}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setConfirmDialog(true)}
                disabled={loading || !validateStep(activeStep)}
                startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
              >
                {loading ? 'Uploading...' : 'Submit Album'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!validateStep(activeStep)}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Album Upload</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to upload an album with {tracks.length} tracks for {selectedUser?.fullName}.
            This action cannot be undone. Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}